import { inject, Injectable, signal } from '@angular/core';
import { BehaviorSubject, firstValueFrom, timeout } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SHARED_WORKER_CONFIG } from './provide-shared-worker';
import {
  AppId,
  SharedWorkerClientMessage,
  SharedWorkerServerMessage,
} from '../models/shared-worker.models';

type BroadcastHandler = (payload: unknown, fromAppId: AppId | null) => void;

function stableClientIdStorageKey(appId: AppId): string {
  return `ebm-shared-worker-client:${appId}`;
}

function resolveStableClientId(appId: AppId): string {
  try {
    const key = stableClientIdStorageKey(appId);
    const existing = sessionStorage.getItem(key);
    if (existing) {
      return existing;
    }
    const created = crypto.randomUUID();
    sessionStorage.setItem(key, created);
    return created;
  } catch {
    return crypto.randomUUID();
  }
}

@Injectable({ providedIn: 'root' })
export class SharedWorkerService {
  private readonly config = inject(SHARED_WORKER_CONFIG, { optional: true });

  private worker: SharedWorker | null = null;
  private port: MessagePort | null = null;
  private clientId: string = crypto.randomUUID();
  private readonly broadcastHandlers = new Map<string, Set<BroadcastHandler>>();
  private primaryReclaimTimer: ReturnType<typeof setTimeout> | null = null;
  private pageHideBound = false;

  readonly supported = signal(typeof SharedWorker !== 'undefined');
  readonly connected = signal(false);
  private readonly presenceReady$ = new BehaviorSubject<boolean>(false);
  readonly appId = signal<AppId | null>(null);
  /** True when this client holds the primary lease for its AppId (one per browser profile). */
  readonly isPrimary = signal(false);
  private readonly openApps = signal<ReadonlySet<AppId>>(new Set());

  private readonly onMessage = (event: MessageEvent<SharedWorkerServerMessage>) => {
    this.handleWorkerMessage(event.data);
  };

  private readonly onWorkerError = () => {
    this.disconnect();
  };

  /** Release the lease before the tab document is gone (reload / close). */
  private readonly onPageHide = () => {
    this.postMessage({ type: 'unregister' });
  };

  connect(): void {
    if (!this.supported() || !this.config || this.connected() || this.worker) {
      return;
    }

    try {
      const workerURL = this.config.workerUrl!;
      this.worker = new SharedWorker(workerURL, {
        type: 'classic',
        name: 'eventq-shared',
      });
      this.port = this.worker.port;
      this.port.start();
      this.port.addEventListener('message', this.onMessage);
      this.worker.addEventListener('error', this.onWorkerError);

      this.clientId = resolveStableClientId(this.config.appId);
      this.appId.set(this.config.appId);
      this.bindPageHide();
      this.postMessage({
        type: 'register',
        clientId: this.clientId,
        appId: this.config.appId,
      });
    } catch {
      this.disconnect();
    }
  }

  disconnect(): void {
    this.clearPrimaryReclaim();
    this.unbindPageHide();
    this.postMessage({ type: 'unregister' });
    if (this.port) this.port.removeEventListener('message', this.onMessage);
    this.worker?.removeEventListener('error', this.onWorkerError);
    this.port = null;
    this.worker = null;
    this.connected.set(false);
    this.isPrimary.set(false);
    this.openApps.set(new Set());
    this.presenceReady$.next(false);
  }

  private bindPageHide(): void {
    if (this.pageHideBound || typeof window === 'undefined') {
      return;
    }
    window.addEventListener('pagehide', this.onPageHide);
    this.pageHideBound = true;
  }

  private unbindPageHide(): void {
    if (!this.pageHideBound || typeof window === 'undefined') {
      return;
    }
    window.removeEventListener('pagehide', this.onPageHide);
    this.pageHideBound = false;
  }

  private handleWorkerMessage(message: SharedWorkerServerMessage): void {
    switch (message.type) {
      case 'registered':
        this.applyPresence(message.apps);
        // Only explicit true is primary — missing/false keeps secondary (fail closed).
        this.isPrimary.set(message.isPrimary === true);
        this.connected.set(true);
        if (message.isPrimary === true) {
          this.clearPrimaryReclaim();
        } else {
          this.schedulePrimaryReclaim();
        }
        break;
      case 'presence-changed':
        this.applyPresence(message.apps);
        break;
      case 'primary-changed':
        if (message.appId === this.appId()) {
          const nowPrimary = message.isPrimary === true;
          this.isPrimary.set(nowPrimary);
          if (nowPrimary) {
            this.clearPrimaryReclaim();
          } else {
            this.schedulePrimaryReclaim();
          }
        }
        break;
      case 'heartbeat-check':
        this.sendHeartbeat();
        break;
      case 'broadcast':
        this.handleBroadcast(message.event, message.payload, message.fromAppId);
        break;
      default:
        break;
    }
  }

  /**
   * If we remain secondary after the worker's primary TTL, re-register so a stale
   * orphaned lease can be stolen and exactly one live tab becomes primary again.
   */
  private schedulePrimaryReclaim(): void {
    this.clearPrimaryReclaim();
    this.primaryReclaimTimer = setTimeout(() => {
      this.primaryReclaimTimer = null;
      if (!this.connected() || this.isPrimary() || !this.config) {
        return;
      }
      this.postMessage({ type: 'unregister' });
      this.postMessage({
        type: 'register',
        clientId: this.clientId,
        appId: this.config.appId,
      });
    }, 3_000);
  }

  private clearPrimaryReclaim(): void {
    if (this.primaryReclaimTimer !== null) {
      clearTimeout(this.primaryReclaimTimer);
      this.primaryReclaimTimer = null;
    }
  }

  private handleBroadcast(event: string, payload: unknown, fromAppId: AppId | null): void {
    const handlers = this.broadcastHandlers.get(event);
    if (!handlers) return;

    for (const handler of handlers) {
      handler(payload, fromAppId);
    }
  }

  private applyPresence(apps: Record<AppId, boolean>): void {
    const open = new Set<AppId>();
    for (const [appId, isOpen] of Object.entries(apps)) {
      if (isOpen) open.add(appId as AppId);
    }
    this.openApps.set(open);
    this.presenceReady$.next(true);
  }

  private sendHeartbeat(): void {
    this.postMessage({ type: 'heartbeat' });
  }

  private postMessage(message: SharedWorkerClientMessage): void {
    this.port?.postMessage(message);
  }

  broadcast<T = unknown>(event: string, payload: T): void {
    this.postMessage({ type: 'broadcast', event, payload });
  }

  on<T = unknown>(
    event: string,
    handler: (payload: T, fromAppId: AppId | null) => void,
  ): () => void {
    const wrapped: BroadcastHandler = (payload, fromAppId) => {
      handler(payload as T, fromAppId);
    };

    const handlers = this.broadcastHandlers.get(event) ?? new Set<BroadcastHandler>();
    handlers.add(wrapped);
    this.broadcastHandlers.set(event, handlers);

    return () => {
      handlers.delete(wrapped);
      if (handlers.size === 0) {
        this.broadcastHandlers.delete(event);
      }
    };
  }

  async isAppOpen(appId: AppId): Promise<boolean> {
    await firstValueFrom(
      this.presenceReady$.pipe(
        filter((ready) => ready),
        timeout({ first: 3000 }),
      ),
    );
    return this.openApps().has(appId);
  }
}
